<script setup lang="ts">
/**
 * Who signs, and where.
 *
 * The space above each rule is deliberate and generous: this is signed by hand
 * on a printed page, and a signature line with nowhere to sign is the kind of
 * detail that sends a document back around the building.
 */
import { useI18n } from 'vue-i18n'

import type { SignaturesSection } from '@/domain/section'

const { t } = useI18n()

defineProps<{ section: SignaturesSection }>()
</script>

<template>
  <div class="signatures">
    <p v-if="section.statement" class="signatures__statement">{{ section.statement }}</p>

    <div class="signatures__row">
      <div v-for="person in section.signatories" :key="person.id" class="signature">
        <div class="signature__space" aria-hidden="true" />
        <div class="signature__rule" />
        <p class="signature__name">{{ person.name || t('canvas.defaults.name') }}</p>
        <p v-if="person.role" class="signature__role">{{ person.role }}</p>
        <p v-if="person.place" class="signature__place">{{ person.place }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.signatures {
  margin-top: 2em;
  break-inside: avoid;
}

.signatures__statement {
  margin-bottom: 2em;
}

.signatures__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(52mm, 1fr));
  gap: 10mm;
}

/* Room to actually sign. */
.signature__space {
  height: 18mm;
}

.signature__rule {
  border-top: 1px solid var(--page-accent);
}

.signature__name {
  margin: 2mm 0 0;
  font-weight: 600;
  text-align: left;
}

.signature__role,
.signature__place {
  margin: 0.6mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 9pt;
  text-align: left;
}
</style>
